// src/pages/CustomerDetails.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./CustomerDetails.css";
import config from "../config";

function CustomerDetails() {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  // DELETE POPUP STATE (userKey ONLY)
  const [deletePopup, setDeletePopup] = useState({
    show: false,
    userKey: null,
    rowIndex: null,
  });

  useEffect(() => {
    fetchCustomerDetails();
  }, []);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${config.BASE_URL}/admin/user-details`);
      const data = Array.isArray(res.data) ? res.data : [];
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/admindashboard");
  };

  // OPEN DELETE CONFIRMATION
  const openDeletePopup = (userKey, rowIndex) => {
    setDeletePopup({
      show: true,
      userKey,
      rowIndex,
    });
  };

  // CLOSE DELETE CONFIRMATION
  const closeDeletePopup = () => {
    setDeletePopup({
      show: false,
      userKey: null,
      rowIndex: null,
    });
  };

  // ✅ DELETE USING PATH VARIABLE (userKey)
  const confirmDelete = async () => {
    const { userKey, rowIndex } = deletePopup;
    if (!userKey) return closeDeletePopup();

    try {
      await axios.delete(`${config.BASE_URL}/admin/delete-user/${userKey}`);

      // remove deleted row from UI
      setCustomers((prev) => prev.filter((_, idx) => idx !== rowIndex));
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      closeDeletePopup();
    }
  };

  const toggleExpand = (userId) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  return (
    <div className="customer-container">
      {/* DELETE CONFIRM POPUP */}
      {deletePopup.show && (
        <div className="popup-overlay">
          <div className="popup-box popup-animate">
            <button className="popup-close" onClick={closeDeletePopup}>
              ✕
            </button>

            <h2 className="popup-title popup-title-failure">Confirm Delete</h2>
            <p className="popup-message">
              Are you sure you want to delete this customer?
            </p>

            <div className="popup-actions">
              <button className="popup-button popup-basic" onClick={closeDeletePopup}>
                Cancel
              </button>
              <button className="popup-button popup-error" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="customer-heading">Customer Details</h2>

      {loading ? (
        <div className="loading-wrapper">
          <div className="spinner"></div>
          <p>Loading customer data...</p>
        </div>
      ) : (
        <>
          <div className="customer-table-wrapper">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Company Name</th>
                  <th>User ID / Password</th>
                  <th>Phone Number (WhatsApp)</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {customers.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-message">
                      No customer data available
                    </td>
                  </tr>
                )}

                {customers.map((user, index) => {
                  const { userId, password, companyName, userKey, devices = [], whatsappDetails = [] } = user;

                  const phoneNumbers = whatsappDetails.length
                    ? whatsappDetails.map((w) => w.whatsappNumber).filter(Boolean).join(", ")
                    : "-";

                  const isOpen = expandedUserId === userId;

                  return (
                    <React.Fragment key={userKey}>
                      <tr>
                        <td>{index + 1}</td>

                        <td className="company-cell-td">
                          <div className="company-cell">
                            <span className="company-name-text">{companyName}</span>
                            <span className="right-arrow" onClick={() => toggleExpand(userId)}>
                              {isOpen ? "(↓)" : "(→)"}
                            </span>
                          </div>
                        </td>

                        <td>
                          {userId} / {password}
                        </td>

                        <td>{phoneNumbers}</td>

                        <td>
                          <button className="delete-btn" onClick={() => openDeletePopup(userKey, index)}>
                            Delete
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="device-details-row">
                          <td colSpan="5">
                            <table className="inner-device-table">
                              <thead>
                                <tr>
                                  <th>Device ID</th>
                                  <th>Device Name (Type)</th>
                                  <th>Linked WhatsApp Number(s)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {devices.length ? (
                                  devices.map((dev, dIndex) => {
                                    // 🔹 LINKED DEVICES LOGIC
                                    const linked = whatsappDetails.filter(
                                      (w) =>
                                        Array.isArray(w.mappedDeviceIds) &&
                                        w.mappedDeviceIds.includes(dev.deviceId)
                                    );

                                    const linkedNumbers = linked.length
                                      ? linked
                                          .map((w) =>
                                            w.whatsappEnabled
                                              ? w.whatsappNumber
                                              : `${w.whatsappNumber} (Disabled)`
                                          )
                                          .join(", ")
                                      : "Not linked";

                                    return (
                                      <tr key={`${dev.deviceId}-${dIndex}`}>
                                        <td>{dev.deviceId}</td>
                                        <td>
                                          {dev.deviceName}
                                          {dev.deviceType && (
                                            <span className="device-type-label"> ({dev.deviceType})</span>
                                          )}
                                        </td>
                                        <td>{linkedNumbers}</td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan="3" className="empty-message">
                                      No devices available for this company
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="customer-actions">
            <button className="add-btn" onClick={handleAdd}>
              Add Device / User
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CustomerDetails;
